import React from 'react';
import { Link } from 'react-router-dom';

const pricingTiers = [
  {
    name: 'Free',
    price: 0,
    currency: 'GBP',
    period: '/month',
    appointments: 15,
    description: 'Perfect for getting started',
    features: [
      '15 appointments per month',
      'Basic booking widget',
      'Email confirmations',
      'Calendar sync',
      'Calendiary branding on widget',
    ],
    branding: true,
    cta: 'Get Started Free',
    popular: false,
  },
  {
    name: 'Starter',
    price: 27,
    currency: 'GBP',
    period: '/month',
    appointments: 100,
    description: 'For growing practices',
    features: [
      '100 appointments per month',
      'Customizable booking widget',
      'Email confirmations',
      'Calendar sync',
      'Remove Calendiary branding',
      'Priority support',
    ],
    branding: false,
    cta: 'Start Free Trial',
    popular: false,
  },
  {
    name: 'Professional',
    price: 57,
    currency: 'GBP',
    period: '/month',
    appointments: 500,
    description: 'For busy practitioners',
    features: [
      '500 appointments per month',
      'Full widget customization',
      'Email confirmations',
      'Calendar sync',
      'Remove Calendiary branding',
      'Priority support',
      'Advanced analytics',
    ],
    branding: false,
    cta: 'Start Free Trial',
    popular: true,
  },
  {
    name: 'Business',
    price: 97,
    currency: 'GBP',
    period: '/month',
    appointments: 1000,
    description: 'For clinics and teams',
    features: [
      '1000 appointments per month',
      'Full widget customization',
      'Email confirmations',
      'Calendar sync',
      'Remove Calendiary branding',
      'Priority support',
      'Advanced analytics',
      'Multi-doctor support',
      'Dedicated account manager',
    ],
    branding: false,
    cta: 'Contact Sales',
    popular: false,
  },
];

const CheckIcon = () => (
  <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const PricingCard = ({ tier }) => {
  const displayPrice = tier.price === 0 ? 'Free' : `£${tier.price}`;

  return (
    <div
      className={`card relative flex flex-col h-full ${tier.popular ? 'border-silver/30 ring-2 ring-silver/20' : ''}`}
      data-tier={tier.name.toLowerCase()}
    >
      {tier.popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
          <span className="bg-silver-gradient text-obsidian font-mono text-xs uppercase tracking-widest px-3 py-1 rounded-full">
            Most Popular
          </span>
        </div>
      )}

      {/* Header */}
      <div className="text-center pb-6 border-b border-light-border dark:border-border">
        <h3 className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-2">
          {tier.name}
        </h3>
        <div className="flex items-baseline justify-center gap-1">
          <span className="font-display text-5xl italic text-light-text dark:text-pure-white">
            {displayPrice}
          </span>
          {tier.price > 0 && (
            <span className="font-mono text-sm text-light-text-secondary dark:text-silver/60">
              {tier.period}
            </span>
          )}
        </div>
        <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mt-2">
          {tier.description}
        </p>
      </div>

      {/* Appointments highlight */}
      <div className="py-4 text-center">
        <span className="font-display text-2xl italic text-light-text dark:text-pure-white">
          {tier.appointments}
        </span>
        <span className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 ml-2">
          appointments/month
        </span>
      </div>

      {/* Features list */}
      <ul className="flex-1 space-y-3 py-4">
        {tier.features.map((feature, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <CheckIcon />
            <span className="font-body text-sm text-slate-500 dark:text-silver/80">
              {feature}
            </span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <div className="pt-6">
        <Link
          to="/register"
          className={`w-full block text-center py-3 px-6 rounded-lg font-mono text-xs uppercase tracking-widest transition-smooth ${
            tier.popular
              ? 'bg-silver-gradient text-obsidian hover:opacity-90'
              : 'border border-light-border dark:border-border text-slate-500 dark:text-silver hover:bg-slate-100 dark:hover:bg-white/5'
          }`}
        >
          {tier.cta}
        </Link>
      </div>
    </div>
  );
};

const Pricing = () => {
  return (
    <div className="min-h-screen py-16 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="font-display text-5xl md:text-6xl italic tracking-tight text-light-text dark:text-pure-white mb-4">
            Simple, Transparent Pricing
          </h1>
          <p className="font-body text-lg text-light-text-secondary dark:text-silver/60 max-w-2xl mx-auto">
            Choose the plan that fits your practice. All plans include a 1% transaction fee on paid appointments.
          </p>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {pricingTiers.map((tier) => (
            <PricingCard key={tier.name} tier={tier} />
          ))}
        </div>

        {/* FAQ Section */}
        <div className="mt-24 text-center">
          <h2 className="font-display text-3xl italic text-light-text dark:text-pure-white mb-8">
            Frequently Asked Questions
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto text-left">
            <div className="card">
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                What is the 1% transaction fee?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                We charge a small 1% fee on paid appointments processed through Calendiary. This helps us maintain the platform and provide excellent service.
              </p>
            </div>
            <div className="card">
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                Can I upgrade or downgrade anytime?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                Yes, you can change your plan at any time. Changes take effect immediately, and we'll prorate any differences.
              </p>
            </div>
            <div className="card">
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                What happens if I exceed my appointment limit?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                We'll notify you when you're approaching your limit. You can upgrade your plan or wait until the next billing cycle.
              </p>
            </div>
            <div className="card">
              <h3 className="font-mono text-sm uppercase tracking-widest text-light-text dark:text-pure-white mb-2">
                Do you offer refunds?
              </h3>
              <p className="font-body text-sm text-light-text-secondary dark:text-silver/60">
                We offer a 14-day money-back guarantee on all paid plans. If you're not satisfied, contact us for a full refund.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
